import { BATTLE_W, GAME_HEIGHT, MAP_FIELD_BOTTOM, UI_TOP_H } from '../config/constants';
import { filterSafeFieldPads, isFieldTurretPlacementSafe } from '../sim/PlacementRules';
import type { MapPropDef } from './mapProps';

export type TerrainPreset = 'rock' | 'snow' | 'lava' | 'acid';
export type WeatherPreset = 'clear' | 'snow' | 'acid-rain' | 'meteors' | 'orbital-lasers' | 'alien-assault';

export interface MapEnvironment {
  terrain: TerrainPreset;
  weather: WeatherPreset;
  /** Authoring intensity, 0..1. Drives visual density and contrast. */
  intensity: number;
}

export const DEFAULT_MAP_ENVIRONMENT: MapEnvironment = {
  terrain: 'rock',
  weather: 'clear',
  intensity: 0.35,
};

export interface PadDef {
  id: number;
  x: number;
  y: number;
}

export interface PathPoint {
  x: number;
  y: number;
}

export interface TerrainMask {
  /** The flat playable surface in screen/world coordinates. */
  bounds: PathPoint[];
  /** Raised terrain volumes that occlude low-flying combat effects. */
  blockers: PathPoint[][];
}

export interface MapDef {
  id: string;
  name: string;
  briefing: string;
  path: PathPoint[];
  pads: PadDef[];
  command: { x: number; y: number; hp: number };
  entry: { x: number; y: number };
  startSupply: number;
  /** Legacy optional second route retained for older map definitions. */
  pathAlt?: PathPoint[];
  entryAlt?: { x: number; y: number };
  /** Full route set for multi-lane maps. Every route ends at the command approach. */
  routes?: PathPoint[][];
  entries?: { x: number; y: number }[];
  /** Raised shelves/platforms. Turrets placed inside them keep their high-ground cover. */
  elevated?: PathPoint[][];
  terrainMask?: TerrainMask;
  /** Decorative objects authored in Map Forge and rendered in battle. */
  props?: MapPropDef[];
  /** Optional commander-authored terrain and weather treatment. */
  environment?: MapEnvironment;
  /** Optional author-tuned depth scaling for sprites on the terrain plane. */
  perspective?: { farScale: number; nearScale: number; tilt?: number; rotation?: number; corners?: [PathPoint, PathPoint, PathPoint, PathPoint] };
  /** Optional custom background plate offset authored in Map Forge. */
  background?: { offsetX?: number; offsetY?: number; scale?: number };
}

/**
 * A commander-authored battlefield layout. These points deliberately live in
 * the rendered 1280x720 battlefield coordinate system (not the old abstract
 * design plane), so a Map Forge edit lines up with the actual terrain plate
 * and is used unchanged by the battle simulation.
 */
export interface MapLayoutEdit {
  routes: PathPoint[][];
  pads: PadDef[];
  entry: PathPoint;
  command: PathPoint;
  /** Decorative objects placed on the terrain. */
  props?: MapPropDef[];
  environment?: MapEnvironment;
  /** Visual alignment aid used by Map Forge; it does not distort gameplay coordinates. */
  plane?: MapPerspectivePlane;
  /** Authoring offset/scale for a custom background plate under the routes. */
  background?: { offsetX?: number; offsetY?: number; scale?: number };
}

export interface MapPerspectivePlane {
  x: number;
  y: number;
  rotation: number;
  tilt: number;
  scale: number;
  visible: boolean;
  /** Sprite scale at the far/top and near/bottom edges of the field. */
  farScale?: number;
  nearScale?: number;
  /** Four editable screen-space corners in TL, TR, BR, BL order. */
  corners?: [PathPoint, PathPoint, PathPoint, PathPoint];
}

/** A commander-created map stored locally in the browser profile. */
export interface UserMapDef {
  id: string;
  name: string;
  backgroundDataUrl?: string;
  layout: MapLayoutEdit;
  createdAt: number;
  updatedAt: number;
  visibility?: 'private' | 'public';
  author?: string;
  publishedAt?: number;
  ratings?: { sum: number; count: number };
  comments?: Array<{ author: string; comment: string; rating: number; timestamp: number }>;
}

/** Returns every enemy route, including the legacy alternate-route fields. */
export function getMapRoutes(map: Pick<MapDef, 'path' | 'pathAlt' | 'routes'>): PathPoint[][] {
  if (map.routes?.length) return map.routes;
  return map.pathAlt ? [map.path, map.pathAlt] : [map.path];
}

/** Hard cap on build pads per map. */
export const PAD_LIMIT = 12;

const MIN_PAD_GAP = 50;

interface Anchor {
  x: number;
  y: number;
  nx: number;
  ny: number;
  /** Preferred side: +1 = left of travel, -1 = right. */
  side: number;
  weight: number;
}

/**
 * Places pads deliberately rather than evenly:
 *  1. sharp bends get a pad on the *inside* of the turn (covers both legs),
 *  2. the middle of long straights get coverage pads,
 *  3. the entry and final approach get one each.
 * Candidates are validated against the road surface and each other.
 */
function makePads(path: PathPoint[], opts: { count?: number; offset?: number; allRoutes?: PathPoint[][] } = {}): PadDef[] {
  const count = opts.count ?? PAD_LIMIT;
  const offset = opts.offset ?? 54;
  const allRoutes = opts.allRoutes ?? [path];

  const seg: number[] = [];
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const d = Math.hypot(path[i + 1].x - path[i].x, path[i + 1].y - path[i].y);
    seg.push(d);
    total += d;
  }

  const pointAt = (dist: number): { p: PathPoint; i: number } => {
    let d = Math.max(0, Math.min(total, dist));
    for (let i = 0; i < seg.length; i++) {
      if (d <= seg[i] || i === seg.length - 1) {
        const f = seg[i] === 0 ? 0 : d / seg[i];
        const a = path[i];
        const b = path[i + 1];
        return { p: { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f }, i };
      }
      d -= seg[i];
    }
    return { p: { ...path[path.length - 1] }, i: seg.length - 1 };
  };

  const normalFor = (i: number): { nx: number; ny: number } => {
    const a = path[i];
    const b = path[i + 1] ?? path[i];
    let nx = -(b.y - a.y);
    let ny = b.x - a.x;
    const l = Math.hypot(nx, ny) || 1;
    nx /= l;
    ny /= l;
    return { nx, ny };
  };

  const anchors: Anchor[] = [];

  // Start with evenly distributed distance samples.  Bend-first selection
  // looked clever on paper, but it made every map favour the first few turns
  // and left long approaches empty.  These samples guarantee longitudinal
  // coverage before the bend/straight heuristics are used as fallbacks.
  const sampleCount = Math.max(4, Math.min(count, 6));
  for (let sample = 0; sample < sampleCount; sample++) {
    const f = (sample + 0.5) / sampleCount;
    const { p, i } = pointAt(total * f);
    const n = normalFor(i);
    anchors.push({
      x: p.x,
      y: p.y,
      nx: n.nx,
      ny: n.ny,
      side: sample % 2 === 0 ? 1 : -1,
      weight: 20 - sample,
    });
  }

  // 1. Bends — sharpest first. Pad on the inside of the turn.
  for (let i = 1; i < path.length - 1; i++) {
    const ax = path[i].x - path[i - 1].x;
    const ay = path[i].y - path[i - 1].y;
    const bx = path[i + 1].x - path[i].x;
    const by = path[i + 1].y - path[i].y;
    const cross = ax * by - ay * bx;
    const dot = ax * bx + ay * by;
    const turn = Math.abs(Math.atan2(cross, dot));
    if (turn < 0.4) continue;
    const n = normalFor(i);
    anchors.push({
      x: path[i].x,
      y: path[i].y,
      nx: n.nx,
      ny: n.ny,
      side: cross > 0 ? 1 : -1,
      weight: turn,
    });
  }
  anchors.sort((a, b) => b.weight - a.weight);

  // 2. Long straights — mid-point coverage, alternating sides.
  const straights = seg
    .map((len, i) => ({ len, i }))
    .filter((s) => s.len > 130)
    .sort((a, b) => b.len - a.len);
  let alt = 1;
  for (const s of straights) {
    let acc = 0;
    for (let k = 0; k < s.i; k++) acc += seg[k];
    const { p } = pointAt(acc + s.len / 2);
    const n = normalFor(s.i);
    anchors.push({ x: p.x, y: p.y, nx: n.nx, ny: n.ny, side: alt, weight: 0.2 });
    alt *= -1;
  }

  // 3. Entry + final approach.
  for (const f of [0.06, 0.94]) {
    const { p, i } = pointAt(total * f);
    const n = normalFor(i);
    anchors.push({ x: p.x, y: p.y, nx: n.nx, ny: n.ny, side: f < 0.5 ? -1 : 1, weight: 0.1 });
  }

  const minX = 44;
  const maxX = BATTLE_W - 44;
  const minY = UI_TOP_H + 44;
  const maxY = MAP_FIELD_BOTTOM - 44;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const pads: PadDef[] = [];
  for (const a of anchors) {
    if (pads.length >= count) break;
    let placed = false;
    for (const side of [a.side, -a.side]) {
      for (const off of [offset, offset + 18, offset + 36, offset - 12]) {
        const cx = clamp(a.x + a.nx * off * side, minX, maxX);
        const cy = clamp(a.y + a.ny * off * side, minY, maxY);
        if (!isFieldTurretPlacementSafe({ x: cx, y: cy }, allRoutes)) continue;
        if (pads.some((q) => Math.hypot(cx - q.x, cy - q.y) < MIN_PAD_GAP)) continue;
        const candidate = { x: Math.round(cx), y: Math.round(cy) };
        if (!isFieldTurretPlacementSafe(candidate, allRoutes)) continue;
        pads.push({ id: pads.length, ...candidate });
        placed = true;
        break;
      }
      if (placed) break;
    }
  }

  // Long, gently curved approaches do not always produce enough sharp-turn
  // anchors. Fill the remaining sockets at measured intervals so every
  // branch has deliberate, readable circles without resorting to a grid.
  if (pads.length < count && total > 0) {
    const samples = Math.max(8, count * 2);
    for (let sample = 1; sample < samples && pads.length < count; sample++) {
      const { p, i } = pointAt((total * sample) / samples);
      const n = normalFor(i);
      for (const side of sample % 2 ? [1, -1] : [-1, 1]) {
        for (const off of [offset, offset + 18, offset + 36, offset - 10]) {
          const cx = clamp(p.x + n.nx * off * side, minX, maxX);
          const cy = clamp(p.y + n.ny * off * side, minY, maxY);
          if (!isFieldTurretPlacementSafe({ x: cx, y: cy }, allRoutes)) continue;
          if (pads.some((q) => Math.hypot(cx - q.x, cy - q.y) < MIN_PAD_GAP)) continue;
          const candidate = { x: Math.round(cx), y: Math.round(cy) };
          if (!isFieldTurretPlacementSafe(candidate, allRoutes)) continue;
          pads.push({ id: pads.length, ...candidate });
          break;
        }
        if (pads.length >= count || pads[pads.length - 1]?.x === Math.round(p.x)) break;
      }
    }
  }

  return pads.map((p, i) => ({ id: i, x: p.x, y: p.y }));
}

/** Convert authored control points into a smooth, simulation-safe polyline. */
// Five points per authored corner keeps the route joined while leaving the
// Map Forge handles far easier to grab and reshape.
export function roundPath(points: PathPoint[], radius = 34, steps = 4): PathPoint[] {
  if (points.length < 3) return points.map((p) => ({ ...p }));
  const out: PathPoint[] = [{ ...points[0] }];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const next = points[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y) || 1;
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y) || 1;
    const r = Math.min(radius, inLen * 0.32, outLen * 0.32);
    const a = { x: cur.x - ((cur.x - prev.x) / inLen) * r, y: cur.y - ((cur.y - prev.y) / inLen) * r };
    const b = { x: cur.x + ((next.x - cur.x) / outLen) * r, y: cur.y + ((next.y - cur.y) / outLen) * r };
    out.push(a);
    for (let k = 1; k <= steps; k++) {
      const t = k / steps;
      const u = 1 - t;
      out.push({
        x: u * u * a.x + 2 * u * t * cur.x + t * t * b.x,
        y: u * u * a.y + 2 * u * t * cur.y + t * t * b.y,
      });
    }
  }
  out.push({ ...points[points.length - 1] });
  return out;
}

function makeRoutePads(routes: PathPoint[][], count = PAD_LIMIT): PadDef[] {
  // Allocate sockets per route first. The former flattened candidate list let
  // the first lane consume nearly every pad, leaving later lanes uncovered.
  const base = Math.floor(count / routes.length);
  const remainder = count % routes.length;
  const candidatesByRoute = routes.map((route, index) => makePads(route, {
    count: base + (index < remainder ? 1 : 0),
    offset: 58,
    allRoutes: routes,
  }));
  // Interleave lanes so the first route cannot consume the global spacing
  // budget before the later routes get any coverage sockets.
  const candidates: PadDef[] = [];
  const maxCandidates = Math.max(0, ...candidatesByRoute.map((lane) => lane.length));
  for (let i = 0; i < maxCandidates; i++) {
    for (const lane of candidatesByRoute) {
      const candidate = lane[i];
      if (candidate) candidates.push(candidate);
    }
  }
  const pads: PadDef[] = [];
  for (const p of candidates) {
    if (pads.some((q) => Math.hypot(p.x - q.x, p.y - q.y) < MIN_PAD_GAP)) continue;
    pads.push({ id: pads.length, x: p.x, y: p.y });
    if (pads.length >= count) break;
  }
  return filterSafeFieldPads(pads, routes).map((pad, id) => ({ ...pad, id }));
}

/** Outpost Kappa — a teaching map with one readable refinery-ring approach. */
const KAPPA_ENTRY = { x: 30, y: 430 };
const KAPPA_ROUTES = [
  roundPath([KAPPA_ENTRY, { x: 120, y: 430 }, { x: 170, y: 300 }, { x: 260, y: 220 }, { x: 390, y: 180 }, { x: 520, y: 210 }, { x: 560, y: 300 }, { x: 680, y: 360 }, { x: 780, y: 320 }, { x: 900, y: 300 }]),
];

// Hand-authored sockets from the commander's map markup.  They deliberately
// sit in the open shelves around the split/rejoin rather than clustering at
// the bastion, while the generated remainder keeps the total at twelve.
const KAPPA_MARKED_PADS: PadDef[] = [
  { id: 0, x: 638, y: 253 },
  { id: 1, x: 582, y: 343 },
  { id: 2, x: 769, y: 381 },
  { id: 3, x: 195, y: 480 },
];
const KAPPA_PADS: PadDef[] = [
  ...filterSafeFieldPads(KAPPA_MARKED_PADS, KAPPA_ROUTES),
  ...makeRoutePads(KAPPA_ROUTES).filter((pad) =>
    !KAPPA_MARKED_PADS.some((marked) => Math.hypot(marked.x - pad.x, marked.y - pad.y) < MIN_PAD_GAP),
  ),
].filter((pad) => isFieldTurretPlacementSafe(pad, KAPPA_ROUTES)).slice(0, PAD_LIMIT).map((pad, id) => ({ ...pad, id }));

export const MAP_KAPPA: MapDef = {
  id: 'kappa',
  name: 'OUTPOST KAPPA',
  briefing:
    'Undermesh activity spikes beneath the Kappa geothermal plant. Hold Bastion-7 until orbital lift clears the reactor core.',
  startSupply: 220,
  command: { x: 900, y: 300, hp: 20 },
  entry: KAPPA_ENTRY,
  path: KAPPA_ROUTES[0],
  routes: KAPPA_ROUTES,
  pads: KAPPA_PADS,
};

/** Helios Drift — an ice-switchback route competes with a wide glacier bowl. */
const HELIOS_ENTRY = { x: 40, y: 470 };
const HELIOS_ROUTES = [
  roundPath([HELIOS_ENTRY, { x: 120, y: 500 }, { x: 220, y: 440 }, { x: 180, y: 340 }, { x: 300, y: 270 }, { x: 440, y: 310 }, { x: 390, y: 190 }, { x: 550, y: 150 }, { x: 700, y: 180 }, { x: 760, y: 230 }, { x: 890, y: 200 }]),
  roundPath([HELIOS_ENTRY, { x: 150, y: 420 }, { x: 260, y: 390 }, { x: 350, y: 430 }, { x: 470, y: 430 }, { x: 560, y: 350 }, { x: 650, y: 420 }, { x: 720, y: 350 }, { x: 780, y: 280 }, { x: 890, y: 200 }]),
];

export const MAP_HELIOS: MapDef = {
  id: 'helios',
  name: 'HELIOS DRIFT',
  briefing:
    'Orion Compact Labs buried a research vault under the ice shelf. Mesh signals are waking the lower labs. Hold the surface relay.',
  startSupply: 240,
  command: { x: 890, y: 200, hp: 18 },
  entry: HELIOS_ENTRY,
  path: HELIOS_ROUTES[0],
  routes: HELIOS_ROUTES,
  pads: makeRoutePads(HELIOS_ROUTES),
};

/** Cinder Reach — a lava stair and a high bridge weave through the same caldera. */
const CINDER_ENTRY = { x: 34, y: 175 };
const CINDER_ROUTES = [
  roundPath([CINDER_ENTRY, { x: 120, y: 150 }, { x: 210, y: 230 }, { x: 160, y: 320 }, { x: 280, y: 360 }, { x: 390, y: 300 }, { x: 500, y: 360 }, { x: 620, y: 330 }, { x: 720, y: 410 }, { x: 820, y: 470 }, { x: 905, y: 430 }]),
  roundPath([CINDER_ENTRY, { x: 120, y: 230 }, { x: 180, y: 180 }, { x: 300, y: 180 }, { x: 360, y: 270 }, { x: 500, y: 210 }, { x: 590, y: 280 }, { x: 700, y: 230 }, { x: 780, y: 320 }, { x: 830, y: 380 }, { x: 905, y: 430 }]),
];

export const MAP_CINDER: MapDef = {
  id: 'cinder',
  name: 'CINDER REACH',
  briefing:
    'Slag refineries on the caldera rim. Thermal vents distort sensors. Mesh borers are chewing through the lower catwalks.',
  startSupply: 230,
  command: { x: 905, y: 430, hp: 16 },
  entry: CINDER_ENTRY,
  path: CINDER_ROUTES[0],
  routes: CINDER_ROUTES,
  pads: makeRoutePads(CINDER_ROUTES),
};

/** Meridian Spire — one route coils around the lift; the other is a brutal gantry switchback. */
const MERIDIAN_ENTRY = { x: 40, y: 190 };
const MERIDIAN_ROUTES = [
  roundPath([MERIDIAN_ENTRY, { x: 150, y: 120 }, { x: 280, y: 150 }, { x: 380, y: 240 }, { x: 300, y: 340 }, { x: 450, y: 390 }, { x: 580, y: 330 }, { x: 520, y: 220 }, { x: 680, y: 140 }, { x: 800, y: 160 }, { x: 920, y: 200 }]),
  roundPath([MERIDIAN_ENTRY, { x: 130, y: 260 }, { x: 230, y: 350 }, { x: 180, y: 470 }, { x: 340, y: 500 }, { x: 470, y: 430 }, { x: 420, y: 310 }, { x: 600, y: 280 }, { x: 700, y: 350 }, { x: 800, y: 280 }, { x: 920, y: 200 }]),
];

export const MAP_MERIDIAN: MapDef = {
  id: 'meridian',
  name: 'MERIDIAN SPIRE',
  briefing:
    'Elevator base at the equatorial anchor. A mesh root system is climbing the foundation pylons. Protect the mass driver.',
  startSupply: 260,
  command: { x: 920, y: 200, hp: 18 },
  entry: MERIDIAN_ENTRY,
  path: MERIDIAN_ROUTES[0],
  routes: MERIDIAN_ROUTES,
  pads: makeRoutePads(MERIDIAN_ROUTES),
};

/** Ashfall Terminal — urban block turns on one route, a market loop on the other. */
const ASHFALL_ENTRY = { x: 40, y: 160 };
const ASHFALL_ROUTES = [
  roundPath([ASHFALL_ENTRY, { x: 140, y: 160 }, { x: 140, y: 280 }, { x: 260, y: 280 }, { x: 260, y: 170 }, { x: 400, y: 170 }, { x: 400, y: 320 }, { x: 540, y: 320 }, { x: 540, y: 220 }, { x: 680, y: 220 }, { x: 720, y: 300 }, { x: 860, y: 300 }]),
  roundPath([ASHFALL_ENTRY, { x: 120, y: 220 }, { x: 220, y: 380 }, { x: 350, y: 380 }, { x: 350, y: 470 }, { x: 520, y: 470 }, { x: 650, y: 400 }, { x: 680, y: 340 }, { x: 760, y: 380 }, { x: 860, y: 300 }]),
];

export const MAP_ASHFALL: MapDef = {
  id: 'ashfall',
  name: 'ASHFALL TERMINAL',
  briefing:
    'Evacuation hub under continuous assault. Streets are open. Mesh elites are probing the perimeter every wave.',
  startSupply: 280,
  command: { x: 860, y: 300, hp: 16 },
  entry: ASHFALL_ENTRY,
  path: ASHFALL_ROUTES[0],
  routes: ASHFALL_ROUTES,
  pads: makeRoutePads(ASHFALL_ROUTES),
};

/** Vault Theta — a short direct needle versus a longer reactor orbit. */
const THETA_ENTRY = { x: 50, y: 320 };
const THETA_ROUTES = [
  roundPath([THETA_ENTRY, { x: 150, y: 320 }, { x: 260, y: 220 }, { x: 420, y: 170 }, { x: 580, y: 220 }, { x: 680, y: 300 }, { x: 760, y: 230 }, { x: 900, y: 300 }]),
  roundPath([THETA_ENTRY, { x: 140, y: 400 }, { x: 280, y: 470 }, { x: 430, y: 470 }, { x: 520, y: 390 }, { x: 470, y: 300 }, { x: 560, y: 250 }, { x: 700, y: 360 }, { x: 780, y: 360 }, { x: 900, y: 300 }]),
];

export const MAP_THETA: MapDef = {
  id: 'theta',
  name: 'VAULT THETA',
  briefing:
    'Subsurface reactor core. Pads are scarce and the lane is short. Colossus-class signatures confirmed. This is a hard hold.',
  startSupply: 300,
  command: { x: 900, y: 300, hp: 14 },
  entry: THETA_ENTRY,
  path: THETA_ROUTES[0],
  routes: THETA_ROUTES,
  pads: makeRoutePads(THETA_ROUTES, 10),
};

// Obsidian is a three-lane foundry: outer ring, lower quarry, and a central
// crosscut that makes the safest pads the most contested ones.
const OBSIDIAN_ENTRY = { x: 30, y: 165 };
const OBSIDIAN_ROUTES = [
  roundPath([OBSIDIAN_ENTRY, { x: 120, y: 100 }, { x: 260, y: 120 }, { x: 360, y: 220 }, { x: 300, y: 320 }, { x: 420, y: 400 }, { x: 560, y: 380 }, { x: 620, y: 280 }, { x: 720, y: 230 }, { x: 800, y: 260 }, { x: 900, y: 300 }]),
  roundPath([OBSIDIAN_ENTRY, { x: 100, y: 240 }, { x: 170, y: 360 }, { x: 260, y: 500 }, { x: 420, y: 500 }, { x: 520, y: 420 }, { x: 640, y: 480 }, { x: 760, y: 430 }, { x: 820, y: 360 }, { x: 900, y: 300 }]),
  roundPath([OBSIDIAN_ENTRY, { x: 100, y: 150 }, { x: 220, y: 250 }, { x: 340, y: 250 }, { x: 430, y: 150 }, { x: 560, y: 150 }, { x: 640, y: 240 }, { x: 720, y: 350 }, { x: 820, y: 330 }, { x: 900, y: 300 }]),
];
export const MAP_OBSIDIAN: MapDef = {
  id: 'obsidian',
  name: 'OBSIDIAN FOUNDRY',
  briefing: 'A geothermal foundry is failing across three basalt shelves. Split the assault, then stop the converging final push.',
  startSupply: 300,
  command: { x: 900, y: 300, hp: 20 },
  entry: OBSIDIAN_ENTRY,
  entries: [OBSIDIAN_ENTRY],
  path: OBSIDIAN_ROUTES[0],
  routes: OBSIDIAN_ROUTES,
  pads: makeRoutePads(OBSIDIAN_ROUTES),
};

const CRYO_ENTRY = { x: 35, y: 120 };
const CRYO_ROUTES = [
  roundPath([CRYO_ENTRY, { x: 130, y: 90 }, { x: 260, y: 150 }, { x: 360, y: 100 }, { x: 470, y: 170 }, { x: 600, y: 120 }, { x: 690, y: 210 }, { x: 780, y: 160 }, { x: 895, y: 210 }]),
  roundPath([CRYO_ENTRY, { x: 100, y: 220 }, { x: 220, y: 320 }, { x: 340, y: 400 }, { x: 470, y: 430 }, { x: 600, y: 380 }, { x: 680, y: 470 }, { x: 780, y: 360 }, { x: 895, y: 210 }]),
  roundPath([CRYO_ENTRY, { x: 150, y: 170 }, { x: 250, y: 260 }, { x: 220, y: 360 }, { x: 330, y: 360 }, { x: 400, y: 280 }, { x: 520, y: 280 }, { x: 580, y: 350 }, { x: 720, y: 300 }, { x: 780, y: 240 }, { x: 895, y: 210 }]),
];
export const MAP_CRYOSTATION: MapDef = {
  id: 'cryostation',
  name: 'CRYOSTATION NADIR',
  briefing: 'The ice shelf has opened three access vectors beneath the research relay. Hold the converged approach before the storm buries it.',
  startSupply: 320,
  command: { x: 895, y: 210, hp: 19 },
  entry: CRYO_ENTRY,
  entries: [CRYO_ENTRY],
  path: CRYO_ROUTES[0],
  routes: CRYO_ROUTES,
  pads: makeRoutePads(CRYO_ROUTES),
};

const FLOOD_ENTRY = { x: 30, y: 150 };
const FLOOD_ROUTES = [
  roundPath([FLOOD_ENTRY, { x: 120, y: 130 }, { x: 180, y: 230 }, { x: 300, y: 230 }, { x: 360, y: 130 }, { x: 500, y: 130 }, { x: 560, y: 240 }, { x: 700, y: 240 }, { x: 760, y: 150 }, { x: 840, y: 220 }, { x: 920, y: 300 }]),
  roundPath([FLOOD_ENTRY, { x: 100, y: 260 }, { x: 180, y: 400 }, { x: 320, y: 470 }, { x: 470, y: 450 }, { x: 550, y: 360 }, { x: 670, y: 420 }, { x: 760, y: 500 }, { x: 860, y: 420 }, { x: 920, y: 300 }]),
  roundPath([FLOOD_ENTRY, { x: 100, y: 170 }, { x: 240, y: 180 }, { x: 300, y: 300 }, { x: 400, y: 360 }, { x: 520, y: 300 }, { x: 620, y: 180 }, { x: 750, y: 180 }, { x: 820, y: 260 }, { x: 920, y: 300 }]),
];
export const MAP_FLOODWAY: MapDef = {
  id: 'floodway',
  name: 'MERIDIAN FLOODWAY',
  briefing: 'Freight platforms are flooding in sequence. Three hostile streams are using the elevated logistics grid to reach the command quay.',
  startSupply: 330,
  command: { x: 920, y: 300, hp: 20 },
  entry: FLOOD_ENTRY,
  entries: [FLOOD_ENTRY],
  path: FLOOD_ROUTES[0],
  routes: FLOOD_ROUTES,
  pads: makeRoutePads(FLOOD_ROUTES),
};

const VERDIGRIS_ENTRY = { x: 30, y: 120 };
const VERDIGRIS_ROUTES = [
  roundPath([VERDIGRIS_ENTRY, { x: 120, y: 80 }, { x: 240, y: 140 }, { x: 300, y: 80 }, { x: 420, y: 130 }, { x: 500, y: 220 }, { x: 620, y: 170 }, { x: 700, y: 260 }, { x: 820, y: 180 }, { x: 905, y: 285 }]),
  roundPath([VERDIGRIS_ENTRY, { x: 100, y: 220 }, { x: 180, y: 330 }, { x: 140, y: 450 }, { x: 300, y: 500 }, { x: 430, y: 430 }, { x: 520, y: 500 }, { x: 660, y: 450 }, { x: 760, y: 380 }, { x: 905, y: 285 }]),
  roundPath([VERDIGRIS_ENTRY, { x: 100, y: 140 }, { x: 220, y: 230 }, { x: 340, y: 180 }, { x: 420, y: 300 }, { x: 540, y: 360 }, { x: 660, y: 310 }, { x: 720, y: 400 }, { x: 820, y: 350 }, { x: 905, y: 285 }]),
];
export const MAP_VERDIGRIS: MapDef = {
  id: 'verdigris',
  name: 'VERDIGRIS BLOOM',
  briefing: 'A bio-industrial canyon is blooming around three broken bridges. Clear every approach before the growth reaches the command platform.',
  startSupply: 340,
  command: { x: 905, y: 285, hp: 18 },
  entry: VERDIGRIS_ENTRY,
  entries: [VERDIGRIS_ENTRY],
  path: VERDIGRIS_ROUTES[0],
  routes: VERDIGRIS_ROUTES,
  pads: makeRoutePads(VERDIGRIS_ROUTES),
};

const UMBRA_ENTRY = { x: 30, y: 155 };
const UMBRA_ROUTES = [
  roundPath([UMBRA_ENTRY, { x: 120, y: 100 }, { x: 250, y: 130 }, { x: 350, y: 230 }, { x: 260, y: 350 }, { x: 400, y: 420 }, { x: 560, y: 390 }, { x: 650, y: 300 }, { x: 580, y: 200 }, { x: 740, y: 140 }, { x: 820, y: 220 }, { x: 910, y: 255 }]),
  roundPath([UMBRA_ENTRY, { x: 90, y: 230 }, { x: 180, y: 300 }, { x: 140, y: 430 }, { x: 300, y: 470 }, { x: 420, y: 390 }, { x: 360, y: 280 }, { x: 520, y: 240 }, { x: 640, y: 350 }, { x: 760, y: 420 }, { x: 840, y: 330 }, { x: 910, y: 255 }]),
  roundPath([UMBRA_ENTRY, { x: 110, y: 160 }, { x: 260, y: 200 }, { x: 390, y: 150 }, { x: 500, y: 180 }, { x: 610, y: 260 }, { x: 700, y: 180 }, { x: 800, y: 160 }, { x: 850, y: 220 }, { x: 910, y: 255 }]),
];
export const MAP_UMBRA: MapDef = {
  id: 'umbra',
  name: 'UMBRA LIFT',
  briefing: 'A storm has cut the elevator basin into three shelves. The final approach is shared; the first warning will come from everywhere.',
  startSupply: 350,
  command: { x: 910, y: 255, hp: 18 },
  entry: UMBRA_ENTRY,
  entries: [UMBRA_ENTRY],
  path: UMBRA_ROUTES[0],
  routes: UMBRA_ROUTES,
  pads: makeRoutePads(UMBRA_ROUTES),
};

// Aurora Citadel — two ice shelves trade long sight-lines before joining at
// the reactor bridge. The elevated polygons are playable high ground and also
// provide real projectile occlusion in the battlefield simulation.
const AURORA_ENTRY = { x: 32, y: 430 };
const AURORA_ROUTES = [
  roundPath([AURORA_ENTRY, { x: 120, y: 470 }, { x: 220, y: 390 }, { x: 310, y: 300 }, { x: 430, y: 250 }, { x: 560, y: 290 }, { x: 650, y: 220 }, { x: 780, y: 180 }, { x: 925, y: 225 }]),
  roundPath([AURORA_ENTRY, { x: 120, y: 360 }, { x: 230, y: 270 }, { x: 350, y: 190 }, { x: 470, y: 160 }, { x: 590, y: 220 }, { x: 700, y: 320 }, { x: 820, y: 290 }, { x: 925, y: 225 }]),
];
export const MAP_AURORA: MapDef = {
  id: 'aurora',
  name: 'AURORA CITADEL',
  briefing: 'A polar relay has split across two ice shelves. Own the high ground, then hold the reactor bridge as both assault lanes converge.',
  startSupply: 360,
  command: { x: 925, y: 225, hp: 20 },
  entry: AURORA_ENTRY,
  entries: [AURORA_ENTRY],
  path: AURORA_ROUTES[0],
  routes: AURORA_ROUTES,
  pads: makeRoutePads(AURORA_ROUTES),
  elevated: [
    [{ x: 72, y: 78 }, { x: 282, y: 78 }, { x: 312, y: 166 }, { x: 192, y: 220 }, { x: 72, y: 174 }],
    [{ x: 684, y: 376 }, { x: 900, y: 364 }, { x: 932, y: 500 }, { x: 740, y: 506 }, { x: 654, y: 440 }],
  ],
};

// Ember Vault — one long switchback lets the commander decide whether to
// invest in the upper furnace shelf or the lower trench firing gallery.
const EMBER_ENTRY = { x: 30, y: 365 };
const EMBER_ROUTES = [
  roundPath([EMBER_ENTRY, { x: 120, y: 430 }, { x: 240, y: 470 }, { x: 320, y: 380 }, { x: 300, y: 260 }, { x: 430, y: 190 }, { x: 560, y: 240 }, { x: 650, y: 350 }, { x: 770, y: 410 }, { x: 850, y: 330 }, { x: 930, y: 275 }]),
];
export const MAP_EMBERFALL: MapDef = {
  id: 'emberfall',
  name: 'EMBER VAULT',
  briefing: 'The furnace vault is failing over a live magma trench. A single switchback lane crosses three elevations; every turret line has a blind side.',
  startSupply: 370,
  command: { x: 930, y: 275, hp: 19 },
  entry: EMBER_ENTRY,
  path: EMBER_ROUTES[0],
  routes: EMBER_ROUTES,
  pads: makeRoutePads(EMBER_ROUTES),
  elevated: [
    [{ x: 86, y: 86 }, { x: 292, y: 86 }, { x: 318, y: 184 }, { x: 208, y: 224 }, { x: 90, y: 188 }],
    [{ x: 420, y: 390 }, { x: 610, y: 378 }, { x: 654, y: 512 }, { x: 470, y: 514 }, { x: 388, y: 452 }],
    [{ x: 724, y: 84 }, { x: 914, y: 84 }, { x: 930, y: 186 }, { x: 806, y: 226 }, { x: 704, y: 172 }],
  ],
};

// Tidelane Array — three staggered causeways enter from one storm gate and
// converge through a raised communications platform. This is deliberately a
// three-lane map with a wide middle crossing instead of another three-way fork.
const TIDELINE_ENTRY = { x: 32, y: 300 };
const TIDELINE_ROUTES = [
  roundPath([TIDELINE_ENTRY, { x: 130, y: 220 }, { x: 270, y: 150 }, { x: 420, y: 170 }, { x: 560, y: 230 }, { x: 700, y: 180 }, { x: 820, y: 220 }, { x: 930, y: 300 }]),
  roundPath([TIDELINE_ENTRY, { x: 160, y: 300 }, { x: 300, y: 300 }, { x: 430, y: 360 }, { x: 570, y: 350 }, { x: 700, y: 300 }, { x: 820, y: 300 }, { x: 930, y: 300 }]),
  roundPath([TIDELINE_ENTRY, { x: 120, y: 390 }, { x: 260, y: 470 }, { x: 400, y: 460 }, { x: 520, y: 390 }, { x: 650, y: 450 }, { x: 790, y: 420 }, { x: 930, y: 300 }]),
];
export const MAP_TIDELINE: MapDef = {
  id: 'tideline',
  name: 'TIDELINE ARRAY',
  briefing: 'A drowned sensor array is still broadcasting. Three storm causeways arrive at the same command quay; keep the middle platform contested.',
  startSupply: 380,
  command: { x: 930, y: 300, hp: 21 },
  entry: TIDELINE_ENTRY,
  entries: [TIDELINE_ENTRY],
  path: TIDELINE_ROUTES[0],
  routes: TIDELINE_ROUTES,
  pads: makeRoutePads(TIDELINE_ROUTES),
  elevated: [
    [{ x: 86, y: 78 }, { x: 268, y: 78 }, { x: 312, y: 160 }, { x: 200, y: 212 }, { x: 88, y: 170 }],
    [{ x: 378, y: 260 }, { x: 596, y: 252 }, { x: 640, y: 360 }, { x: 530, y: 402 }, { x: 402, y: 360 }],
    [{ x: 700, y: 396 }, { x: 906, y: 390 }, { x: 936, y: 514 }, { x: 762, y: 516 }, { x: 680, y: 458 }],
  ],
};

/**
 * Legacy projection for the early abstract/top-down campaign layouts.
 *
 * New terrain plates must use native rendered coordinates through
 * TERRAIN_REFERENCE_LAYOUTS / Map Forge instead: a single mathematical
 * transform cannot know where a particular plate has cliffs, bridges or
 * roads. The legacy path is retained only so existing maps remain playable
 * while they are progressively re-authored in the editor.
 */
export function projectIsometricPoint(point: PathPoint): PathPoint {
  const centerX = BATTLE_W * 0.5;
  const fieldTop = UI_TOP_H;
  const fieldBottom = MAP_FIELD_BOTTOM;
  const centerY = fieldTop + (fieldBottom - fieldTop) * 0.5;
  const dy = point.y - centerY;

  // The authored routes historically stopped around x=900, leaving a large
  // unused shelf beside the command base on wide displays. Expand the route
  // plane from its left entry edge so the extra horizontal space becomes
  // meaningful path, while preserving the entry position and all vertical
  // gameplay coordinates.
  const routeLeft = 30;
  // Pull every command approach toward the right edge. 1.30 leaves a clear
  // safety margin for the bastion sprite and its roof hardpoints while making
  // the full-width battlefield feel intentionally authored instead of empty.
  const routeStretch = 1.30;
  const stretchedX = routeLeft + (point.x - routeLeft) * routeStretch;
  const stretchedDx = stretchedX - centerX;

  // Oblique iso basis: depth compresses vertically while each screen axis
  // contributes a small component to the other, matching the angled terrain
  // shelves in the authored backgrounds without pushing endpoints off-field.
  return {
    x: stretchedX + dy * 0.16,
    y: centerY + stretchedDx * 0.08 + dy * 0.74,
  };
}

function projectMapToTerrain(map: MapDef): MapDef {
  const project = (point: PathPoint): PathPoint => projectIsometricPoint(point);
  const routes = getMapRoutes(map).map((route) => route.map(project));
  const padBudget = map.id === 'theta' ? 10 : PAD_LIMIT;
  const markedPads = map.id === 'kappa' ? map.pads.filter((pad) => pad.id < 4).map((pad) => ({ ...pad, ...project(pad) })) : [];
  const generatedPads = makeRoutePads(routes, padBudget);
  const pads: PadDef[] = [];
  for (const candidate of [...markedPads, ...generatedPads]) {
    if (!isFieldTurretPlacementSafe(candidate, routes)) continue;
    if (pads.some((pad) => Math.hypot(pad.x - candidate.x, pad.y - candidate.y) < MIN_PAD_GAP)) continue;
    pads.push({ id: pads.length, x: Math.round(candidate.x), y: Math.round(candidate.y) });
    if (pads.length >= padBudget) break;
  }
  const fieldBottom = MAP_FIELD_BOTTOM;
  const command = project(map.command);
  const commandRadius = 58;
  const projected: MapDef = {
    ...map,
    path: routes[0] ?? map.path.map(project),
    routes,
    pads,
    command: { ...map.command, ...command },
    entry: project(map.entry),
    elevated: (map.elevated ?? []).map((shelf) => shelf.map(project)),
    terrainMask: {
      bounds: [
        { x: 0, y: UI_TOP_H },
        { x: BATTLE_W, y: UI_TOP_H },
        { x: BATTLE_W, y: fieldBottom },
        { x: 0, y: fieldBottom },
      ],
      blockers: [
        ...(map.elevated ?? []).map((shelf) => shelf.map(project)),
        [
          { x: command.x - commandRadius, y: command.y - commandRadius },
          { x: command.x + commandRadius, y: command.y - commandRadius },
          { x: command.x + commandRadius, y: command.y + commandRadius },
          { x: command.x - commandRadius, y: command.y + commandRadius },
        ],
        [
          { x: 0, y: fieldBottom - 24 },
          { x: BATTLE_W, y: fieldBottom - 24 },
          { x: BATTLE_W, y: fieldBottom },
          { x: 0, y: fieldBottom },
        ],
      ],
    },
  };
  if (map.pathAlt) projected.pathAlt = map.pathAlt.map(project);
  if (map.entryAlt) projected.entryAlt = project(map.entryAlt);
  if (map.entries) projected.entries = map.entries.map(project);
  return projected;
}

const RAW_MAPS: Record<string, MapDef> = {
  kappa: MAP_KAPPA,
  helios: MAP_HELIOS,
  cinder: MAP_CINDER,
  meridian: MAP_MERIDIAN,
  ashfall: MAP_ASHFALL,
  theta: MAP_THETA,
  obsidian: MAP_OBSIDIAN,
  cryostation: MAP_CRYOSTATION,
  floodway: MAP_FLOODWAY,
  verdigris: MAP_VERDIGRIS,
  umbra: MAP_UMBRA,
  aurora: MAP_AURORA,
  emberfall: MAP_EMBERFALL,
  tideline: MAP_TIDELINE,
};

// Mutate the authored map objects in place as well as the registry so direct
// imports (MAP_KAPPA, MAP_HELIOS, etc.) receive the same projected geometry.
for (const map of Object.values(RAW_MAPS)) {
  Object.assign(map, projectMapToTerrain(map));
}

export const MAPS: Record<string, MapDef> = RAW_MAPS;

const MAP_LAYOUT_STORAGE_KEY = 'apogee-hold-map-layouts-v1';
const USER_MAP_STORAGE_KEY = 'apogee-hold-user-maps-v1';
let sharedMapLayouts: Record<string, MapLayoutEdit> = {};

const clonePoint = <T extends PathPoint>(point: T): T => ({ ...point });

function cloneLayout(layout: MapLayoutEdit): MapLayoutEdit {
  return {
    routes: layout.routes.map((route) => route.map(clonePoint)),
    pads: layout.pads.map(clonePoint),
    entry: clonePoint(layout.entry),
    command: clonePoint(layout.command),
    props: layout.props?.map((prop) => ({ ...prop })),
    environment: layout.environment ? { ...layout.environment } : undefined,
    plane: layout.plane ? {
      ...layout.plane,
      corners: layout.plane.corners?.map(clonePoint) as MapPerspectivePlane['corners'],
    } : undefined,
    background: layout.background ? { ...layout.background } : undefined,
  };
}

function makeTerrainMask(map: MapDef, command: PathPoint): TerrainMask {
  const fieldBottom = MAP_FIELD_BOTTOM;
  const commandRadius = 58;
  return {
    bounds: [
      { x: 0, y: UI_TOP_H }, { x: BATTLE_W, y: UI_TOP_H },
      { x: BATTLE_W, y: fieldBottom }, { x: 0, y: fieldBottom },
    ],
    blockers: [
      ...(map.elevated ?? []).map((shelf) => shelf.map(clonePoint)),
      [
        { x: command.x - commandRadius, y: command.y - commandRadius },
        { x: command.x + commandRadius, y: command.y - commandRadius },
        { x: command.x + commandRadius, y: command.y + commandRadius },
        { x: command.x - commandRadius, y: command.y + commandRadius },
      ],
      [
        { x: 0, y: fieldBottom - 24 }, { x: BATTLE_W, y: fieldBottom - 24 },
        { x: BATTLE_W, y: fieldBottom }, { x: 0, y: fieldBottom },
      ],
    ],
  };
}

/**
 * Aurora is the reference pass for terrain-aware routes. The route follows
 * the visible lower snow shelf and upper relay bridge, with intentional
 * right-angle service turns where it changes elevation. It replaces the old
 * one-size-fits-all projection for this map and is editable in Map Forge.
 */
const TERRAIN_REFERENCE_LAYOUTS: Record<string, MapLayoutEdit> = {
  aurora: {
    entry: { x: 68, y: 444 },
    command: { x: 1162, y: 356 },
    routes: [
      [
        { x: 68, y: 444 }, { x: 176, y: 432 }, { x: 280, y: 414 },
        { x: 354, y: 362 }, { x: 354, y: 324 }, { x: 468, y: 324 },
        { x: 564, y: 350 }, { x: 660, y: 384 }, { x: 780, y: 384 },
        { x: 852, y: 340 }, { x: 852, y: 300 }, { x: 980, y: 300 },
        { x: 1088, y: 332 }, { x: 1162, y: 356 },
      ],
      [
        { x: 68, y: 444 }, { x: 118, y: 394 }, { x: 118, y: 346 },
        { x: 230, y: 300 }, { x: 344, y: 274 }, { x: 460, y: 240 },
        // This section rides the visible elevated relay bridge rather than
        // cutting across the chasm in screen space.
        { x: 560, y: 224 }, { x: 660, y: 234 }, { x: 760, y: 244 },
        { x: 860, y: 260 }, { x: 962, y: 272 },
        { x: 1068, y: 304 }, { x: 1162, y: 356 },
      ],
    ],
    pads: [],
  },
};

// Keep the public map registry coherent too. Older code and developer tools
// can import MAP_AURORA directly, so leaving its abandoned generic projection
// in place would create two competing versions of the same battlefield.
// Aurora is now the native, terrain-authored reference for future generators.
const AURORA_REFERENCE = TERRAIN_REFERENCE_LAYOUTS.aurora;
Object.assign(MAP_AURORA, {
  path: AURORA_REFERENCE.routes[0].map(clonePoint),
  pathAlt: AURORA_REFERENCE.routes[1].map(clonePoint),
  routes: AURORA_REFERENCE.routes.map((route) => route.map(clonePoint)),
  entry: clonePoint(AURORA_REFERENCE.entry),
  entries: AURORA_REFERENCE.routes.map((route) => clonePoint(route[0])),
  command: { ...MAP_AURORA.command, ...AURORA_REFERENCE.command },
  pads: makeRoutePads(AURORA_REFERENCE.routes),
  // The plate already contains its physical ice shelves. Do not draw stale
  // translucent polygons from the generic top-down pass over that terrain.
  elevated: [],
});
MAP_AURORA.terrainMask = makeTerrainMask(MAP_AURORA, MAP_AURORA.command);

function layoutForMap(map: MapDef): MapLayoutEdit {
  const reference = TERRAIN_REFERENCE_LAYOUTS[map.id];
  const routes = reference?.routes ?? getMapRoutes(map);
  const entry = reference?.entry ?? map.entry;
  const command = reference?.command ?? map.command;
  const pads = reference?.pads.length
    ? reference.pads
    : makeRoutePads(routes, map.id === 'theta' ? 10 : PAD_LIMIT);
  return cloneLayout({ routes, pads, entry, command, plane: defaultPerspectivePlane() });
}

function defaultPerspectivePlane(): MapPerspectivePlane {
  return {
    x: BATTLE_W * 0.5,
    y: (UI_TOP_H + MAP_FIELD_BOTTOM) * 0.5,
    rotation: 0,
    tilt: 0.766,
    scale: 1,
    visible: true,
    farScale: 0.68,
    nearScale: 1.1,
    // A visible, terrain-sized quadrilateral gives the author four handles
    // immediately, even when opening a legacy layout with no plane metadata.
    corners: [
      { x: 84, y: 238 }, { x: BATTLE_W - 104, y: 212 },
      { x: BATTLE_W - 74, y: MAP_FIELD_BOTTOM - 42 }, { x: 64, y: MAP_FIELD_BOTTOM - 18 },
    ],
  };
}

function readSavedLayouts(): Record<string, MapLayoutEdit> {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(MAP_LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, MapLayoutEdit>;
    return Object.fromEntries(Object.entries(parsed).filter(([, value]) => (
      Array.isArray(value?.routes) && Array.isArray(value?.pads) && !!value?.entry && !!value?.command
    )));
  } catch {
    return {};
  }
}

/**
 * Apply layouts fetched from the shared campaign store. These take precedence
 * over browser-local drafts so every player sees the same published geometry.
 */
export function applyCampaignMapLayouts(layouts: Record<string, MapLayoutEdit>): void {
  sharedMapLayouts = Object.fromEntries(Object.entries(layouts).flatMap(([mapId, layout]) => {
    const map = MAPS[mapId];
    if (!map || !layout || !Array.isArray(layout.routes)) return [];
    return [[mapId, sanitiseMapLayout(map, layout)]] as const;
  }));
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('apogee-campaign-layouts-loaded'));
}

/** Update the in-memory shared copy immediately after a successful save. */
export function cacheCampaignMapLayout(mapId: string, layout: MapLayoutEdit): void {
  const map = MAPS[mapId];
  if (map) sharedMapLayouts[mapId] = sanitiseMapLayout(map, layout);
}

/** Save an authored layout locally as a resilient offline fallback. */
export function saveMapLayout(mapId: string, layout: MapLayoutEdit): void {
  const map = MAPS[mapId];
  if (!map) return;
  const safe = sanitiseMapLayout(map, layout);
  // Keep the current session in sync even if the shared API is unavailable;
  // a fresh session will replace this with the server copy during boot.
  sharedMapLayouts[mapId] = safe;
  const layouts = readSavedLayouts();
  layouts[mapId] = safe;
  try {
    localStorage.setItem(MAP_LAYOUT_STORAGE_KEY, JSON.stringify(layouts));
    // Let an already-open command screen lazily refresh its local preview
    // without forcing a synchronous texture or network generation step.
    window.dispatchEvent(new CustomEvent('apogee-map-layout-saved', { detail: { mapId } }));
  } catch {
    // The editor remains usable for this session even if browser storage is full.
  }
}

export function resetMapLayout(mapId: string): void {
  const layouts = readSavedLayouts();
  delete layouts[mapId];
  try {
    localStorage.setItem(MAP_LAYOUT_STORAGE_KEY, JSON.stringify(layouts));
  } catch {
    // Best-effort reset for restrictive browser-storage environments.
  }
}

/** Returns the editable current layout for an individual map. */
export function getMapLayout(mapId: string): MapLayoutEdit {
  const map = MAPS[mapId] ?? MAPS.kappa;
  const saved = sharedMapLayouts[map.id] ?? readSavedLayouts()[map.id];
  return sanitiseMapLayout(map, saved ?? layoutForMap(map));
}

/**
 * Ensure field pads stay off every route and keep start/base attached to each
 * lane. This is the same contract the live tower-placement system uses.
 */
export function sanitiseMapLayout(map: MapDef, candidate: MapLayoutEdit): MapLayoutEdit {
  const fieldBottom = MAP_FIELD_BOTTOM;
  const within = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
  const clamp = (point: PathPoint): PathPoint => ({
    // The imported background is the authoring boundary. Keep every point
    // inside its actual image instead of silently adding a 28px dead zone.
    x: Math.round(within(Number.isFinite(point?.x) ? point.x : 0, 0, BATTLE_W)),
    y: Math.round(within(Number.isFinite(point?.y) ? point.y : UI_TOP_H, UI_TOP_H, fieldBottom)),
  });
  const command = clamp(candidate?.command ?? { x: BATTLE_W - 100, y: 420 });
  const routes = (candidate.routes ?? [])
    .filter((route) => Array.isArray(route) && route.length >= 2)
    .map((route) => {
      const cleaned = route
        .filter((point): point is PathPoint => !!point && Number.isFinite(point.x) && Number.isFinite(point.y))
        .map(clamp);
      if (cleaned.length < 2) return null;
      cleaned[cleaned.length - 1] = { ...command };
      return cleaned;
    })
    .filter((route): route is PathPoint[] => Array.isArray(route) && route.length >= 2);
  const fallbackRoutes = routes.length ? routes : layoutForMap(map).routes;
  // Each route owns its own start point. `entry` remains the legacy primary
  // entry for systems which have not yet consumed the full entries array.
  const entry = { ...(fallbackRoutes[0]?.[0] ?? clamp(candidate.entry)) };
  const pads = filterSafeFieldPads(candidate.pads
    .filter((pad) => !!pad && Number.isFinite(pad.x) && Number.isFinite(pad.y))
    .map(clamp), fallbackRoutes)
    .filter((pad, index, list) => list.findIndex((other) => Math.hypot(pad.x - other.x, pad.y - other.y) < MIN_PAD_GAP) === index)
    .slice(0, map.id === 'theta' ? 10 : PAD_LIMIT)
    .map((pad, id) => ({ id, ...pad }));
  const props = (candidate.props ?? []).flatMap((prop) => {
    if (!prop || typeof prop.kind !== 'string') return [];
    const point = clamp(prop);
    const rotation = Number.isFinite(prop.rotation) ? prop.rotation : 0;
    const scale = within(Number.isFinite(prop.scale) ? prop.scale : 0.12, 0.05, 0.5);
    const depth = within(Number.isFinite(prop.depth) ? prop.depth : 1, 0, 5);
    const height = prop.height === 'high' || prop.height === 'medium' || prop.height === 'low' ? prop.height : 'low';
    return [{
      id: prop.id || `prop-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      kind: prop.kind,
      x: point.x,
      y: point.y,
      rotation,
      scale,
      depth,
      height,
      blocksLineOfSight: prop.blocksLineOfSight === true,
    } as MapPropDef];
  });
  const rawPlane = candidate.plane ?? defaultPerspectivePlane();
  const planeDefaults = defaultPerspectivePlane();
  const rawCorners = rawPlane.corners ?? planeDefaults.corners!;
  // Perspective handles are deliberately free-placement controls.  Do not
  // run them through the authored-point quantiser: saving a map must preserve
  // the exact sub-pixel plane geometry the author just positioned.
  const clampPlane = (point: PathPoint): PathPoint => ({
    x: within(point.x, 0, BATTLE_W),
    y: within(point.y, UI_TOP_H, fieldBottom),
  });
  const safeCorners = rawCorners.map(clampPlane) as MapPerspectivePlane['corners'];
  const plane: MapPerspectivePlane = {
    x: within(rawPlane.x, 0, BATTLE_W),
    y: within(rawPlane.y, UI_TOP_H, fieldBottom),
    rotation: Number.isFinite(rawPlane.rotation) ? rawPlane.rotation : 0,
    tilt: within(Number.isFinite(rawPlane.tilt) ? rawPlane.tilt : 0.766, 0.3, 0.82),
    scale: within(Number.isFinite(rawPlane.scale) ? rawPlane.scale : 1, 0.55, 1.45),
    visible: rawPlane.visible !== false,
    farScale: within(Number.isFinite(rawPlane.farScale) ? rawPlane.farScale! : 0.68, 0.45, 0.95),
    nearScale: within(Number.isFinite(rawPlane.nearScale) ? rawPlane.nearScale! : 1.1, 0.9, 1.45),
    corners: safeCorners,
  };
  const environment: MapEnvironment = {
    terrain: candidate.environment?.terrain === 'snow' || candidate.environment?.terrain === 'lava' || candidate.environment?.terrain === 'acid'
      ? candidate.environment.terrain
      : 'rock',
    weather: candidate.environment?.weather === 'snow' || candidate.environment?.weather === 'acid-rain' || candidate.environment?.weather === 'meteors' || candidate.environment?.weather === 'orbital-lasers' || candidate.environment?.weather === 'alien-assault'
      ? candidate.environment.weather
      : 'clear',
    intensity: within(Number.isFinite(candidate.environment?.intensity) ? candidate.environment!.intensity : DEFAULT_MAP_ENVIRONMENT.intensity, 0, 1),
  };
  const background = candidate.background && (Number.isFinite(candidate.background.offsetX) || Number.isFinite(candidate.background.offsetY) || Number.isFinite(candidate.background.scale))
    ? {
      offsetX: within(Number(candidate.background.offsetX) || 0, -900, 900),
      offsetY: within(Number(candidate.background.offsetY) || 0, -500, 500),
      scale: within(Number(candidate.background.scale) || 1, 0.5, 2.5),
    }
    : undefined;
  return { routes: fallbackRoutes, pads, entry, command, props, plane, environment, background };
}

function readUserMaps(): UserMapDef[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const parsed = JSON.parse(localStorage.getItem(USER_MAP_STORAGE_KEY) ?? '[]') as UserMapDef[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((map) => !!map?.id && !!map?.name && !!map?.layout).map((map) => ({
      ...map,
      visibility: map.visibility === 'public' ? 'public' : 'private',
      layout: sanitiseMapLayout(MAPS.aurora, map.layout),
    }));
  } catch {
    return [];
  }
}

/** Return the locally authored map library, newest maps first. */
export function listUserMaps(): UserMapDef[] {
  return readUserMaps().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getUserMap(id: string): UserMapDef | undefined {
  return listUserMaps().find((map) => map.id === id);
}

export function saveUserMap(userMap: UserMapDef): void {
  const safe: UserMapDef = {
    ...userMap,
    layout: sanitiseMapLayout(MAPS.aurora, userMap.layout),
    updatedAt: Date.now(),
  };
  const maps = readUserMaps().filter((map) => map.id !== safe.id);
  maps.push(safe);
  try { localStorage.setItem(USER_MAP_STORAGE_KEY, JSON.stringify(maps)); } catch { /* storage is best effort */ }
}

export function deleteUserMap(id: string): void {
  try { localStorage.setItem(USER_MAP_STORAGE_KEY, JSON.stringify(readUserMaps().filter((map) => map.id !== id))); } catch { /* best effort */ }
}

/** Seed a new user map from Aurora's terrain-aware authoring layout. */
function blankUserLayout(laneCount = 1): MapLayoutEdit {
  const count = Math.max(1, Math.min(4, Math.round(laneCount)));
  const command = { x: BATTLE_W - 100, y: 420 };
  const routes = Array.from({ length: count }, (_, index) => {
    const y = Math.max(140, Math.min(600, 420 + (index - (count - 1) / 2) * 92));
    const entry = { x: 72, y };
    const points = [0.22, 0.45, 0.68].map((t) => ({
      x: Math.round(entry.x + (command.x - entry.x) * t),
      y: Math.round(y + (command.y - y) * (t * 0.7)),
    }));
    return [entry, ...points, { ...command }];
  });
  return cloneLayout({ routes, pads: [], entry: { ...routes[0][0] }, command, props: [], plane: defaultPerspectivePlane(), environment: { ...DEFAULT_MAP_ENVIRONMENT } });
}

export function createUserMap(name: string, backgroundDataUrl?: string, fromScratch = false, laneCount = 1): UserMapDef {
  const now = Date.now();
  return {
    id: `user-${now}-${Math.random().toString(36).slice(2, 7)}`,
    name: name.trim().slice(0, 32) || 'UNTITLED FIELD',
    backgroundDataUrl,
    layout: fromScratch ? blankUserLayout(laneCount) : getMapLayout('aurora'),
    createdAt: now,
    updatedAt: now,
    visibility: 'private',
  };
}

/** Convert a local map to the runtime MapDef consumed by BattleScene. */
export function userMapToMapDef(userMap: UserMapDef): MapDef {
  const layout = sanitiseMapLayout(MAPS.aurora, userMap.layout);
  const routes = layout.routes.map((route) => route.map(clonePoint));
  const base = MAPS.aurora;
  return {
    ...base,
    id: userMap.id,
    name: userMap.name,
    briefing: 'COMMANDER-AUTHORED FIELD  ·  USER MAP LIBRARY',
    path: routes[0],
    routes,
    pathAlt: routes[1],
    entry: { ...layout.entry },
    entries: routes.map((route) => ({ ...route[0] })),
    pads: layout.pads.map((pad) => ({ ...pad })),
    props: layout.props?.map((prop) => ({ ...prop })),
    environment: layout.environment ? { ...layout.environment } : undefined,
    command: { ...base.command, ...layout.command },
    perspective: { farScale: layout.plane?.farScale ?? 0.68, nearScale: layout.plane?.nearScale ?? 1.1, tilt: layout.plane?.tilt ?? 0.766, rotation: layout.plane?.rotation ?? 0, corners: layout.plane?.corners },
    background: layout.background ? { ...layout.background } : undefined,
    terrainMask: makeTerrainMask(base, layout.command),
  };
}

/** A clone of a map with the reference or player-authored runtime layout applied. */
export function getMapWithLayout(mapId: string): MapDef {
  const base = MAPS[mapId] ?? MAPS.kappa;
  const layout = getMapLayout(base.id);
  const routes = layout.routes.map((route) => route.map(clonePoint));
  const command = { ...base.command, ...layout.command };
  return {
    ...base,
    path: routes[0],
    routes,
    pathAlt: routes[1],
    entry: { ...layout.entry },
    entries: routes.map((route) => ({ ...route[0] })),
    pads: layout.pads.map(clonePoint),
    props: layout.props?.map((prop) => ({ ...prop })),
    environment: layout.environment ? { ...layout.environment } : undefined,
    command,
    perspective: { farScale: layout.plane?.farScale ?? 0.68, nearScale: layout.plane?.nearScale ?? 1.1, tilt: layout.plane?.tilt ?? 0.766, rotation: layout.plane?.rotation ?? 0, corners: layout.plane?.corners },
    terrainMask: makeTerrainMask(base, command),
  };
}

